// ===== tweaks-panel.jsx =====

// tweaks-panel.jsx
// Reusable Tweaks shell + form-control helpers.
//
// Owns the host protocol (listens for __activate_edit_mode / __deactivate_edit_mode,
// posts __edit_mode_available / __edit_mode_set_keys / __edit_mode_dismissed) so
// individual prototypes don't re-roll it. Ships a consistent set of controls so you
// don't hand-draw <input type="range">, segmented radios, steppers, etc.
//
// Usage (in an HTML file that loads React + Babel):
//
//   const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
//     "primaryColor": "#D97757",
//     "fontSize": 16,
//     "density": "regular",
//     "dark": false
//   }/*EDITMODE-END*/;
//
//   function App() {
//     const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);
//     return (
//       <div style={{ fontSize: t.fontSize, color: t.primaryColor }}>
//         Hello
//         <TweaksPanel>
//           <TweakSection label="Typography" />
//           <TweakSlider label="Font size" value={t.fontSize} min={10} max={32} unit="px"
//                        onChange={(v) => setTweak('fontSize', v)} />
//           <TweakRadio  label="Density" value={t.density}
//                        options={['compact', 'regular', 'comfy']}
//                        onChange={(v) => setTweak('density', v)} />
//           <TweakSection label="Theme" />
//           <TweakColor  label="Primary" value={t.primaryColor}
//                        onChange={(v) => setTweak('primaryColor', v)} />
//           <TweakToggle label="Dark mode" value={t.dark}
//                        onChange={(v) => setTweak('dark', v)} />
//         </TweaksPanel>
//       </div>
//     );
//   }
//
// ─────────────────────────────────────────────────────────────────────────────

const __TWEAKS_STYLE = `
  .twk-panel{position:fixed;right:16px;bottom:16px;z-index:2147483646;width:280px;
    max-height:calc(100vh - 32px);display:flex;flex-direction:column;
    background:rgba(250,249,247,.78);color:#29261b;
    -webkit-backdrop-filter:blur(24px) saturate(160%);backdrop-filter:blur(24px) saturate(160%);
    border:.5px solid rgba(255,255,255,.6);border-radius:14px;
    box-shadow:0 1px 0 rgba(255,255,255,.5) inset,0 12px 40px rgba(0,0,0,.18);
    font:11.5px/1.4 ui-sans-serif,system-ui,-apple-system,sans-serif;overflow:hidden}
  .twk-hd{display:flex;align-items:center;justify-content:space-between;
    padding:10px 8px 10px 14px;cursor:move;user-select:none}
  .twk-hd b{font-size:12px;font-weight:600;letter-spacing:.01em}
  .twk-x{appearance:none;border:0;background:transparent;color:rgba(41,38,27,.55);
    width:22px;height:22px;border-radius:6px;cursor:default;font-size:13px;line-height:1}
  .twk-x:hover{background:rgba(0,0,0,.06);color:#29261b}
  .twk-body{padding:2px 14px 14px;display:flex;flex-direction:column;gap:10px;
    overflow-y:auto;overflow-x:hidden;min-height:0;
    scrollbar-width:thin;scrollbar-color:rgba(0,0,0,.15) transparent}
  .twk-body::-webkit-scrollbar{width:8px}
  .twk-body::-webkit-scrollbar-track{background:transparent;margin:2px}
  .twk-body::-webkit-scrollbar-thumb{background:rgba(0,0,0,.15);border-radius:4px;
    border:2px solid transparent;background-clip:content-box}
  .twk-body::-webkit-scrollbar-thumb:hover{background:rgba(0,0,0,.25);
    border:2px solid transparent;background-clip:content-box}
  .twk-row{display:flex;flex-direction:column;gap:5px}
  .twk-row-h{flex-direction:row;align-items:center;justify-content:space-between;gap:10px}
  .twk-lbl{display:flex;justify-content:space-between;align-items:baseline;
    color:rgba(41,38,27,.72)}
  .twk-lbl>span:first-child{font-weight:500}
  .twk-val{color:rgba(41,38,27,.5);font-variant-numeric:tabular-nums}

  .twk-sect{font-size:10px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;
    color:rgba(41,38,27,.45);padding:10px 0 0}
  .twk-sect:first-child{padding-top:0}

  .twk-field{appearance:none;width:100%;height:26px;padding:0 8px;
    border:.5px solid rgba(0,0,0,.1);border-radius:7px;
    background:rgba(255,255,255,.6);color:inherit;font:inherit;outline:none}
  .twk-field:focus{border-color:rgba(0,0,0,.25);background:rgba(255,255,255,.85)}
  select.twk-field{padding-right:22px;
    background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path fill='rgba(0,0,0,.5)' d='M0 0h10L5 6z'/></svg>");
    background-repeat:no-repeat;background-position:right 8px center}

  .twk-slider{appearance:none;-webkit-appearance:none;width:100%;height:4px;margin:6px 0;
    border-radius:999px;background:rgba(0,0,0,.12);outline:none}
  .twk-slider::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;
    width:14px;height:14px;border-radius:50%;background:#fff;
    border:.5px solid rgba(0,0,0,.12);box-shadow:0 1px 3px rgba(0,0,0,.2);cursor:default}
  .twk-slider::-moz-range-thumb{width:14px;height:14px;border-radius:50%;
    background:#fff;border:.5px solid rgba(0,0,0,.12);box-shadow:0 1px 3px rgba(0,0,0,.2);cursor:default}

  .twk-seg{position:relative;display:flex;padding:2px;border-radius:8px;
    background:rgba(0,0,0,.06);user-select:none}
  .twk-seg-thumb{position:absolute;top:2px;bottom:2px;border-radius:6px;
    background:rgba(255,255,255,.9);box-shadow:0 1px 2px rgba(0,0,0,.12);
    transition:left .15s cubic-bezier(.3,.7,.4,1),width .15s}
  .twk-seg.dragging .twk-seg-thumb{transition:none}
  .twk-seg button{appearance:none;position:relative;z-index:1;flex:1;border:0;
    background:transparent;color:inherit;font:inherit;font-weight:500;min-height:22px;
    border-radius:6px;cursor:default;padding:4px 6px;line-height:1.2;
    overflow-wrap:anywhere}

  .twk-toggle{position:relative;width:32px;height:18px;border:0;border-radius:999px;
    background:rgba(0,0,0,.15);transition:background .15s;cursor:default;padding:0}
  .twk-toggle[data-on="1"]{background:#34c759}
  .twk-toggle i{position:absolute;top:2px;left:2px;width:14px;height:14px;border-radius:50%;
    background:#fff;box-shadow:0 1px 2px rgba(0,0,0,.25);transition:transform .15s}
  .twk-toggle[data-on="1"] i{transform:translateX(14px)}

  .twk-num{display:flex;align-items:center;height:26px;padding:0 0 0 8px;
    border:.5px solid rgba(0,0,0,.1);border-radius:7px;background:rgba(255,255,255,.6)}
  .twk-num-lbl{font-weight:500;color:rgba(41,38,27,.6);cursor:ew-resize;
    user-select:none;padding-right:8px}
  .twk-num input{flex:1;min-width:0;height:100%;border:0;background:transparent;
    font:inherit;font-variant-numeric:tabular-nums;text-align:right;padding:0 8px 0 0;
    outline:none;color:inherit;-moz-appearance:textfield}
  .twk-num input::-webkit-inner-spin-button,.twk-num input::-webkit-outer-spin-button{
    -webkit-appearance:none;margin:0}
  .twk-num-unit{padding-right:8px;color:rgba(41,38,27,.45)}

  .twk-btn{appearance:none;height:26px;padding:0 12px;border:0;border-radius:7px;
    background:rgba(0,0,0,.78);color:#fff;font:inherit;font-weight:500;cursor:default}
  .twk-btn:hover{background:rgba(0,0,0,.88)}
  .twk-btn.secondary{background:rgba(0,0,0,.06);color:inherit}
  .twk-btn.secondary:hover{background:rgba(0,0,0,.1)}

  .twk-swatch{appearance:none;-webkit-appearance:none;width:56px;height:22px;
    border:.5px solid rgba(0,0,0,.1);border-radius:6px;padding:0;cursor:default;
    background:transparent;flex-shrink:0}
  .twk-swatch::-webkit-color-swatch-wrapper{padding:0}
  .twk-swatch::-webkit-color-swatch{border:0;border-radius:5.5px}
  .twk-swatch::-moz-color-swatch{border:0;border-radius:5.5px}
`;

// ── useTweaks ───────────────────────────────────────────────────────────────
// Single source of truth for tweak values. setTweak persists via the host
// (__edit_mode_set_keys → host rewrites the EDITMODE block on disk).
function useTweaks(defaults) {
  const [values, setValues] = React.useState(defaults);
  // Accepts either setTweak('key', value) or setTweak({ key: value, ... }) so a
  // useState-style call doesn't write a "[object Object]" key into the persisted
  // JSON block.
  const setTweak = React.useCallback((keyOrEdits, val) => {
    const edits = typeof keyOrEdits === 'object' && keyOrEdits !== null
      ? keyOrEdits : { [keyOrEdits]: val };
    setValues((prev) => ({ ...prev, ...edits }));
    window.parent.postMessage({ type: '__edit_mode_set_keys', edits }, '*');
  }, []);
  return [values, setTweak];
}

// ── TweaksPanel ─────────────────────────────────────────────────────────────
// Floating shell. Registers the protocol listener BEFORE announcing
// availability — if the announce ran first, the host's activate could land
// before our handler exists and the toolbar toggle would silently no-op.
// The close button posts __edit_mode_dismissed so the host's toolbar toggle
// flips off in lockstep; the host echoes __deactivate_edit_mode back which
// is what actually hides the panel.
function TweaksPanel({ title = 'Tweaks', children }) {
  const [open, setOpen] = React.useState(false);
  const dragRef = React.useRef(null);
  const offsetRef = React.useRef({ x: 16, y: 16 });
  const PAD = 16;

  const clampToViewport = React.useCallback(() => {
    const panel = dragRef.current;
    if (!panel) return;
    const w = panel.offsetWidth, h = panel.offsetHeight;
    const maxRight = Math.max(PAD, window.innerWidth - w - PAD);
    const maxBottom = Math.max(PAD, window.innerHeight - h - PAD);
    offsetRef.current = {
      x: Math.min(maxRight, Math.max(PAD, offsetRef.current.x)),
      y: Math.min(maxBottom, Math.max(PAD, offsetRef.current.y)),
    };
    panel.style.right = offsetRef.current.x + 'px';
    panel.style.bottom = offsetRef.current.y + 'px';
  }, []);

  React.useEffect(() => {
    if (!open) return;
    clampToViewport();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', clampToViewport);
      return () => window.removeEventListener('resize', clampToViewport);
    }
    const ro = new ResizeObserver(clampToViewport);
    ro.observe(document.documentElement);
    return () => ro.disconnect();
  }, [open, clampToViewport]);

  React.useEffect(() => {
    const onMsg = (e) => {
      const t = e?.data?.type;
      if (t === '__activate_edit_mode') setOpen(true);
      else if (t === '__deactivate_edit_mode') setOpen(false);
    };
    window.addEventListener('message', onMsg);
    window.parent.postMessage({ type: '__edit_mode_available' }, '*');
    return () => window.removeEventListener('message', onMsg);
  }, []);

  const dismiss = () => {
    setOpen(false);
    window.parent.postMessage({ type: '__edit_mode_dismissed' }, '*');
  };

  const onDragStart = (e) => {
    const panel = dragRef.current;
    if (!panel) return;
    const r = panel.getBoundingClientRect();
    const sx = e.clientX, sy = e.clientY;
    const startRight = window.innerWidth - r.right;
    const startBottom = window.innerHeight - r.bottom;
    const move = (ev) => {
      offsetRef.current = {
        x: startRight - (ev.clientX - sx),
        y: startBottom - (ev.clientY - sy),
      };
      clampToViewport();
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  if (!open) return null;
  return (
    <>
      <style>{__TWEAKS_STYLE}</style>
      <div ref={dragRef} className="twk-panel" data-noncommentable=""
           style={{ right: offsetRef.current.x, bottom: offsetRef.current.y }}>
        <div className="twk-hd" onMouseDown={onDragStart}>
          <b>{title}</b>
          <button className="twk-x" aria-label="Close tweaks"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={dismiss}>✕</button>
        </div>
        <div className="twk-body">{children}</div>
      </div>
    </>
  );
}

// ── Layout helpers ──────────────────────────────────────────────────────────

function TweakSection({ label, children }) {
  return (
    <>
      <div className="twk-sect">{label}</div>
      {children}
    </>
  );
}

function TweakRow({ label, value, children, inline = false }) {
  return (
    <div className={inline ? 'twk-row twk-row-h' : 'twk-row'}>
      <div className="twk-lbl">
        <span>{label}</span>
        {value != null && <span className="twk-val">{value}</span>}
      </div>
      {children}
    </div>
  );
}

// ── Controls ────────────────────────────────────────────────────────────────

function TweakSlider({ label, value, min = 0, max = 100, step = 1, unit = '', onChange }) {
  return (
    <TweakRow label={label} value={`${value}${unit}`}>
      <input type="range" className="twk-slider" min={min} max={max} step={step}
             value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </TweakRow>
  );
}

function TweakToggle({ label, value, onChange }) {
  return (
    <div className="twk-row twk-row-h">
      <div className="twk-lbl"><span>{label}</span></div>
      <button type="button" className="twk-toggle" data-on={value ? '1' : '0'}
              role="switch" aria-checked={!!value}
              onClick={() => onChange(!value)}><i /></button>
    </div>
  );
}

function TweakRadio({ label, value, options, onChange }) {
  const trackRef = React.useRef(null);
  const [dragging, setDragging] = React.useState(false);
  const opts = options.map((o) => (typeof o === 'object' ? o : { value: o, label: o }));
  const idx = Math.max(0, opts.findIndex((o) => o.value === value));
  const n = opts.length;

  // The active value is read by pointer-move handlers attached for the lifetime
  // of a drag — ref it so a stale closure doesn't fire onChange for every move.
  const valueRef = React.useRef(value);
  valueRef.current = value;

  const segAt = (clientX) => {
    const r = trackRef.current.getBoundingClientRect();
    const inner = r.width - 4;
    const i = Math.floor(((clientX - r.left - 2) / inner) * n);
    return opts[Math.max(0, Math.min(n - 1, i))].value;
  };

  const onPointerDown = (e) => {
    setDragging(true);
    const v0 = segAt(e.clientX);
    if (v0 !== valueRef.current) onChange(v0);
    const move = (ev) => {
      if (!trackRef.current) return;
      const v = segAt(ev.clientX);
      if (v !== valueRef.current) onChange(v);
    };
    const up = () => {
      setDragging(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <TweakRow label={label}>
      <div ref={trackRef} role="radiogroup" onPointerDown={onPointerDown}
           className={dragging ? 'twk-seg dragging' : 'twk-seg'}>
        <div className="twk-seg-thumb"
             style={{ left: `calc(2px + ${idx} * (100% - 4px) / ${n})`,
                      width: `calc((100% - 4px) / ${n})` }} />
        {opts.map((o) => (
          <button key={o.value} type="button" role="radio" aria-checked={o.value === value}>
            {o.label}
          </button>
        ))}
      </div>
    </TweakRow>
  );
}

function TweakSelect({ label, value, options, onChange }) {
  return (
    <TweakRow label={label}>
      <select className="twk-field" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => {
          const v = typeof o === 'object' ? o.value : o;
          const l = typeof o === 'object' ? o.label : o;
          return <option key={v} value={v}>{l}</option>;
        })}
      </select>
    </TweakRow>
  );
}

function TweakText({ label, value, placeholder, onChange }) {
  return (
    <TweakRow label={label}>
      <input className="twk-field" type="text" value={value} placeholder={placeholder}
             onChange={(e) => onChange(e.target.value)} />
    </TweakRow>
  );
}

function TweakNumber({ label, value, min, max, step = 1, unit = '', onChange }) {
  const clamp = (n) => {
    if (min != null && n < min) return min;
    if (max != null && n > max) return max;
    return n;
  };
  const startRef = React.useRef({ x: 0, val: 0 });
  const onScrubStart = (e) => {
    e.preventDefault();
    startRef.current = { x: e.clientX, val: value };
    const decimals = (String(step).split('.')[1] || '').length;
    const move = (ev) => {
      const dx = ev.clientX - startRef.current.x;
      const raw = startRef.current.val + dx * step;
      const snapped = Math.round(raw / step) * step;
      onChange(clamp(Number(snapped.toFixed(decimals))));
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  return (
    <div className="twk-num">
      <span className="twk-num-lbl" onPointerDown={onScrubStart}>{label}</span>
      <input type="number" value={value} min={min} max={max} step={step}
             onChange={(e) => onChange(clamp(Number(e.target.value)))} />
      {unit && <span className="twk-num-unit">{unit}</span>}
    </div>
  );
}

function TweakColor({ label, value, onChange }) {
  return (
    <div className="twk-row twk-row-h">
      <div className="twk-lbl"><span>{label}</span></div>
      <input type="color" className="twk-swatch" value={value}
             onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function TweakButton({ label, onClick, secondary = false }) {
  return (
    <button type="button" className={secondary ? 'twk-btn secondary' : 'twk-btn'}
            onClick={onClick}>{label}</button>
  );
}

Object.assign(window, {
  useTweaks, TweaksPanel, TweakSection, TweakRow,
  TweakSlider, TweakToggle, TweakRadio, TweakSelect,
  TweakText, TweakNumber, TweakColor, TweakButton,
});


// ===== hero.jsx =====
// hero.jsx — Hero section with live-feeling telemetry
const { useState, useEffect, useRef, useMemo } = React;

function useTick(ms = 1000) {
  const [t, setT] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setT(x => x + 1), ms);
    return () => clearInterval(id);
  }, [ms]);
  return t;
}

// Smooth-ish synthetic signal
function signal(seed, t, opts = {}) {
  const { base = 0.5, amp = 0.35, freq = 0.6, noise = 0.04 } = opts;
  const a = Math.sin((t + seed * 7.3) * freq) * amp * 0.6;
  const b = Math.sin((t + seed * 3.1) * freq * 2.7) * amp * 0.3;
  const n = (Math.sin((t + seed) * 17.7) * 0.5 + Math.sin((t + seed) * 31.1) * 0.5) * noise;
  return Math.max(0.04, Math.min(0.96, base + a + b + n));
}

function Waveform({ seed = 1, color = "var(--oxide)", height = 64, points = 80, label, value, unit }) {
  const [t, setT] = useState(0);
  useEffect(() => {
    let raf, start;
    const loop = (ts) => {
      if (!start) start = ts;
      setT((ts - start) / 1000);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  const W = 320, H = height;
  const pts = useMemo(() => {
    return Array.from({ length: points }).map((_, i) => {
      const x = (i / (points - 1)) * W;
      const y = H - signal(seed + i * 0.05, t + i * 0.04) * H;
      return [x, y];
    });
  }, [t, seed, points, H]);

  const path = pts.map((p, i) => (i === 0 ? `M${p[0]},${p[1]}` : `L${p[0]},${p[1]}`)).join(" ");
  const area = `${path} L${W},${H} L0,${H} Z`;
  const last = pts[pts.length - 1];

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
        <span className="ulabel">{label}</span>
        <span className="mono" style={{ fontSize: 13, color: "var(--bone)" }}>
          <span style={{ color }}>{value}</span>
          <span className="muted" style={{ marginLeft: 4 }}>{unit}</span>
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: "100%", height, display: "block" }}>
        <defs>
          <linearGradient id={`g-${seed}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.35" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* gridlines */}
        {[0.25, 0.5, 0.75].map((p) => (
          <line key={p} x1="0" x2={W} y1={H * p} y2={H * p} stroke="rgba(232,228,220,.08)" strokeWidth="1" />
        ))}
        <path d={area} fill={`url(#g-${seed})`} />
        <path d={path} fill="none" stroke={color} strokeWidth="1.25" />
        <circle cx={last[0]} cy={last[1]} r="3" fill={color} />
        <circle cx={last[0]} cy={last[1]} r="6" fill="none" stroke={color} strokeOpacity=".4" />
      </svg>
    </div>
  );
}

function Gauge({ label, value, unit, max = 100, warn = 80, seed = 0 }) {
  const [t, setT] = useState(0);
  useEffect(() => {
    let raf, start;
    const loop = (ts) => { if (!start) start = ts; setT((ts - start) / 1000); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);
  const v = Math.round(signal(seed, t, { base: value / max, amp: 0.06, freq: 0.4 }) * max);
  const pct = v / max;
  const color = v >= warn ? "var(--oxide)" : "var(--bone)";
  const ang = -135 + pct * 270;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
      <svg width="64" height="64" viewBox="0 0 64 64">
        <circle cx="32" cy="32" r="26" fill="none" stroke="var(--rule)" strokeWidth="1" />
        {/* tick marks */}
        {Array.from({ length: 19 }).map((_, i) => {
          const a = (-135 + (i / 18) * 270) * Math.PI / 180;
          const x1 = 32 + Math.cos(a) * 22;
          const y1 = 32 + Math.sin(a) * 22;
          const x2 = 32 + Math.cos(a) * 26;
          const y2 = 32 + Math.sin(a) * 26;
          const isMajor = i % 6 === 0;
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={isMajor ? "var(--bone-2)" : "var(--rule)"} strokeWidth="1" />;
        })}
        {/* needle */}
        <line
          x1="32" y1="32"
          x2={32 + Math.cos(ang * Math.PI / 180) * 22}
          y2={32 + Math.sin(ang * Math.PI / 180) * 22}
          stroke={color} strokeWidth="1.5"
        />
        <circle cx="32" cy="32" r="3" fill={color} />
      </svg>
      <div style={{ minWidth: 0 }}>
        <div className="ulabel" style={{ marginBottom: 4 }}>{label}</div>
        <div className="mono" style={{ fontSize: 18, color: "var(--bone)" }}>
          {v}<span className="muted" style={{ fontSize: 11, marginLeft: 4 }}>{unit}</span>
        </div>
      </div>
    </div>
  );
}

function ScrollingLog() {
  const lines = [
    ["08:42:11.043", "OK", "Spindle bearing temp within nominal"],
    ["08:42:09.881", "OK", "Hydraulic pressure stable @ 142 bar"],
    ["08:42:07.221", "WARN", "Coolant flow variance +2.3σ — flagged"],
    ["08:42:04.110", "OK", "Vibration FFT — no anomalous harmonics"],
    ["08:41:58.770", "OK", "Servo current draw within envelope"],
    ["08:41:52.402", "ALERT", "Predicted bearing fatigue: 148h ±12h"],
    ["08:41:48.119", "OK", "Tool wear estimate 31% of replacement"],
    ["08:41:42.005", "OK", "Acoustic signature matches baseline"],
    ["08:41:36.776", "OK", "Power factor 0.94 — corrected"],
    ["08:41:30.221", "WARN", "Lubrication interval approaching (72h)"],
  ];
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setOffset(o => (o + 1) % lines.length), 2200);
    return () => clearInterval(id);
  }, []);
  const ordered = [...lines.slice(offset), ...lines.slice(0, offset)];
  return (
    <div className="mono" style={{ fontSize: 11, lineHeight: 1.7, color: "var(--bone-2)" }}>
      {ordered.slice(0, 6).map(([ts, lvl, msg], i) => (
        <div key={ts + i} className="grid-log" style={{
          gap: 10, padding: "3px 0",
          opacity: 1 - i * 0.13,
          borderBottom: i < 5 ? "1px solid var(--rule-2)" : "none",
        }}>
          <span style={{ color: "var(--bone-2)" }}>{ts}</span>
          <span style={{
            color: lvl === "ALERT" ? "var(--oxide)" : lvl === "WARN" ? "var(--amber)" : "var(--patina)",
            letterSpacing: ".1em"
          }}>{lvl}</span>
          <span style={{ color: "var(--bone)" }}>{msg}</span>
        </div>
      ))}
    </div>
  );
}

function MachineSchematic() {
  // 3-axis drill line — front elevation, technical drawing style.
  // X = horizontal travel along gantry, Y = depth into page (shown as offset),
  // Z = vertical drill plunge.
  const [t, setT] = useState(0);
  useEffect(() => {
    let raf, start;
    const loop = (ts) => { if (!start) start = ts; setT((ts - start) / 1000); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Coordinate system: X 80..440 (head travel), Z 70..130 (drill plunge), workpiece around y=170
  // Drill line motion plan:
  // Head traverses to a hole position, dwells while plunging, retracts, moves on.
  // One full hole cycle = CYCLE seconds, distributed:
  //   travel 45% · approach 5% · plunge 20% · dwell 5% · retract 20% · settle 5%
  const xMin = 100, xMax = 420;
  const holes = [130, 180, 230, 280, 330, 380];
  const CYCLE = 6.5;       // seconds per hole — slower
  const RETURN = 4.0;      // extra slow return-to-home phase (seconds)
  const homeX = xMin;      // park position at left of work envelope
  const totalLoop = CYCLE * holes.length + RETURN;
  const tLoop = t % totalLoop;

  // Eased X traversal: ease-in-out
  const easeInOut = (k) => k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;

  let headX;
  let plungePhase;
  let idx;       // currently-targeted hole index (last hole during return)
  let local;     // 0..1 within current sub-phase (kept for hole-state logic)

  if (tLoop < CYCLE * holes.length) {
    // Drilling phases: one CYCLE per hole
    const phase = tLoop / CYCLE;
    idx = Math.floor(phase);
    local = phase - idx;
    const targetX = holes[idx];
    const prevX = idx === 0 ? homeX : holes[idx - 1];

    if (local < 0.45) {
      headX = prevX + (targetX - prevX) * easeInOut(local / 0.45);
    } else {
      headX = targetX;
    }

    if (local < 0.5) plungePhase = 0;
    else if (local < 0.7) plungePhase = easeInOut((local - 0.5) / 0.2);
    else if (local < 0.75) plungePhase = 1;
    else if (local < 0.95) plungePhase = 1 - easeInOut((local - 0.75) / 0.2);
    else plungePhase = 0;
  } else {
    // Return-to-home phase: slow ease back to start, no plunge
    const r = (tLoop - CYCLE * holes.length) / RETURN;  // 0..1
    const lastHole = holes[holes.length - 1];
    headX = lastHole + (homeX - lastHole) * easeInOut(r);
    plungePhase = 0;
    idx = holes.length - 1;       // all holes complete — keep them rendered as done
    local = 1;                     // past the drill window for hole-state purposes
  }

  const overHole = local >= 0.45 && local < 0.95;
  const drilling = local >= 0.6 && local < 0.78;   // active cutting window for chips

  const headZ = 92 + plungePhase * 22;
  const tipY = headZ + 28 + plungePhase * 6;

  // Live readouts
  const xMM = Math.round((headX - xMin) / (xMax - xMin) * 2400);
  const zMM = Math.round(plungePhase * 38);
  const yMM = 0;
  const rpm = 4200 + Math.round(Math.sin(t * 1.3) * 60);

  return (
    <svg viewBox="0 0 520 280" style={{ width: "100%", height: "auto", display: "block" }}>
      <defs>
        <pattern id="hatch1" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="var(--rule)" strokeWidth="1" />
        </pattern>
        <pattern id="hatchWP" width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(135)">
          <line x1="0" y1="0" x2="0" y2="5" stroke="var(--bone-2)" strokeWidth="0.5" strokeOpacity=".5" />
        </pattern>
        <marker id="arrL" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M0,0 L10,5 L0,10 Z" fill="var(--bone-2)" />
        </marker>
      </defs>

      {/* === Floor / baseplate === */}
      <rect x="40" y="232" width="440" height="14" fill="url(#hatch1)" stroke="var(--bone-2)" strokeWidth="1" />

      {/* Stanchions / frame columns */}
      <rect x="50" y="40" width="14" height="200" fill="none" stroke="var(--bone)" strokeWidth="1" />
      <line x1="53" y1="40" x2="53" y2="240" stroke="var(--rule)" />
      <line x1="61" y1="40" x2="61" y2="240" stroke="var(--rule)" />
      <rect x="456" y="40" width="14" height="200" fill="none" stroke="var(--bone)" strokeWidth="1" />
      <line x1="459" y1="40" x2="459" y2="240" stroke="var(--rule)" />
      <line x1="467" y1="40" x2="467" y2="240" stroke="var(--rule)" />

      {/* === X-axis gantry beam (top) === */}
      <rect x="64" y="48" width="392" height="28" fill="none" stroke="var(--bone)" strokeWidth="1" />
      {/* Linear rail (top rail of gantry) */}
      <line x1="72" y1="56" x2="448" y2="56" stroke="var(--bone-2)" strokeWidth="1" />
      <line x1="72" y1="68" x2="448" y2="68" stroke="var(--bone-2)" strokeWidth="1" />
      {/* Ball screw (dashed centerline) */}
      <line x1="72" y1="62" x2="448" y2="62" stroke="var(--rule)" strokeWidth="1" strokeDasharray="3,3" />
      {/* Servo motor at end of ball screw */}
      <rect x="32" y="50" width="18" height="24" fill="none" stroke="var(--bone)" strokeWidth="1" />
      <circle cx="41" cy="62" r="4" fill="none" stroke="var(--bone-2)" />
      <text x="41" y="86" textAnchor="middle" fontSize="7" fontFamily="JetBrains Mono" fill="var(--bone-2)">SRV-X</text>

      {/* X position readout markers along gantry */}
      {[0, 0.25, 0.5, 0.75, 1].map(p => {
        const x = xMin + (xMax - xMin) * p;
        return (
          <g key={p}>
            <line x1={x} y1="78" x2={x} y2="82" stroke="var(--rule)" />
            <text x={x} y="92" textAnchor="middle" fontSize="6" fontFamily="JetBrains Mono" fill="var(--bone-2)">
              {Math.round(p * 2400)}
            </text>
          </g>
        );
      })}

      {/* === Z-axis carriage (the drill head) === */}
      <g style={{ transition: "none" }}>
        {/* Z column / vertical rail attached to X carriage */}
        <rect x={headX - 18} y="44" width="36" height="14" fill="var(--paper)" stroke="var(--bone)" strokeWidth="1" />
        <rect x={headX - 16} y="50" width="32" height="2" fill="var(--bone-2)" />
        {/* Z guide rails */}
        <line x1={headX - 14} y1="76" x2={headX - 14} y2="142" stroke="var(--bone-2)" strokeWidth="1" />
        <line x1={headX + 14} y1="76" x2={headX + 14} y2="142" stroke="var(--bone-2)" strokeWidth="1" />
        {/* Z ball screw */}
        <line x1={headX} y1="76" x2={headX} y2="142" stroke="var(--rule)" strokeDasharray="2,2" />

        {/* Spindle motor body */}
        <rect x={headX - 22} y={headZ - 18} width="44" height="22" fill="var(--paper)" stroke="var(--bone)" strokeWidth="1" />
        <line x1={headX - 18} y1={headZ - 14} x2={headX + 18} y2={headZ - 14} stroke="var(--rule)" />
        <line x1={headX - 18} y1={headZ - 8} x2={headX + 18} y2={headZ - 8} stroke="var(--rule)" />
        <circle cx={headX + 16} cy={headZ - 13} r="1.5" fill={overHole ? "var(--oxide)" : "var(--patina)"} />

        {/* Spindle nose / chuck */}
        <rect x={headX - 10} y={headZ + 4} width="20" height="14" fill="var(--paper)" stroke="var(--bone)" strokeWidth="1" />
        <line x1={headX - 6} y1={headZ + 4} x2={headX - 6} y2={headZ + 18} stroke="var(--rule)" />
        <line x1={headX + 6} y1={headZ + 4} x2={headX + 6} y2={headZ + 18} stroke="var(--rule)" />

        {/* Drill bit (twist) */}
        <line x1={headX} y1={headZ + 18} x2={headX} y2={tipY} stroke="var(--bone)" strokeWidth="2" />
        {/* flute marks */}
        {[0, 1, 2, 3].map(i => {
          const y = headZ + 21 + i * 3;
          if (y > tipY - 1) return null;
          return <line key={i} x1={headX - 1.5} y1={y} x2={headX + 1.5} y2={y + 1.5} stroke="var(--paper)" strokeWidth=".8" />;
        })}
        {/* tip */}
        <path d={`M${headX - 2},${tipY - 2} L${headX},${tipY + 2} L${headX + 2},${tipY - 2} Z`} fill="var(--bone)" />

        {/* Sparks / chips when plunging */}
        {plungePhase > 0.3 && (
          <g opacity={plungePhase}>
            {[-1, 1].map(dir => (
              <line key={dir}
                x1={headX} y1={tipY}
                x2={headX + dir * (3 + Math.random() * 4)}
                y2={tipY + 2 + Math.random() * 3}
                stroke="var(--oxide)" strokeWidth="1" />
            ))}
            <circle cx={headX} cy={tipY} r={1.5 + plungePhase * 1.5} fill="var(--oxide)" opacity={plungePhase * 0.7} />
          </g>
        )}
      </g>

      {/* === Workpiece (steel beam being drilled) === */}
      <rect x="80" y="170" width="360" height="34" fill="url(#hatchWP)" stroke="var(--bone)" strokeWidth="1" />
      <line x1="80" y1="178" x2="440" y2="178" stroke="var(--rule)" />
      <line x1="80" y1="196" x2="440" y2="196" stroke="var(--rule)" />
      {/* Holes already drilled */}
      {holes.map((hx, i) => {
        const isCurrent = i === idx;
        const isDone = i < idx || (isCurrent && local > 0.7);
        return (
          <g key={hx}>
            {isDone && <circle cx={hx} cy="187" r="3" fill="var(--paper)" stroke="var(--bone-2)" strokeWidth="1" />}
            {!isDone && !isCurrent && <circle cx={hx} cy="187" r="2.5" fill="none" stroke="var(--rule)" strokeDasharray="1,1" />}
            {isCurrent && !isDone && (
              <circle cx={hx} cy="187" r="3" fill="none" stroke="var(--oxide)" strokeDasharray="1,1" />
            )}
          </g>
        );
      })}

      {/* === Y axis indicator (depth-into-page reference) === */}
      <g>
        <rect x="78" y="206" width="364" height="6" fill="none" stroke="var(--rule)" />
        <line x1="80" y1="209" x2="440" y2="209" stroke="var(--rule)" strokeDasharray="1,3" />
      </g>

      {/* === Axis legends === */}
      <g fontFamily="JetBrains Mono" fontSize="8" fill="var(--bone-2)">
        {/* X arrow under gantry */}
        <line x1="100" y1="100" x2="420" y2="100" stroke="var(--bone-2)" markerEnd="url(#arrL)" />
        <text x="260" y="112" textAnchor="middle">X · TRAVEL</text>
        {/* Z arrow on right of head */}
        <line x1={headX + 36} y1="60" x2={headX + 36} y2="140" stroke="var(--bone-2)" markerEnd="url(#arrL)" />
        <text x={headX + 40} y="110" textAnchor="start">Z</text>
        {/* Y arrow into page (perspective hint at top-right) */}
        <line x1="430" y1="262" x2="470" y2="252" stroke="var(--bone-2)" markerEnd="url(#arrL)" />
        <text x="478" y="252" textAnchor="start">Y</text>
      </g>

      {/* === Sensor callouts (kept, but re-targeted at drill-line components) === */}
      {[
        { x: headX, y: headZ - 8, lx: 60, ly: 28, num: "01", label: "VIB / RPM" },
        { x: headX, y: tipY, lx: 60, ly: 222, num: "02", label: "ACOUSTIC" },
        { x: 458, y: 60, lx: 502, ly: 60, num: "03", label: "SRV-Z TORQUE" },
        { x: 41, y: 62, lx: 16, ly: 116, num: "04", label: "SRV-X CURRENT" },
      ].map((c, i) => (
        <g key={i}>
          <circle cx={c.x} cy={c.y} r="3.5" fill="var(--oxide)" />
          <circle cx={c.x} cy={c.y} r="8" fill="none" stroke="var(--oxide)" strokeOpacity=".4" />
          <line x1={c.x} y1={c.y} x2={c.lx} y2={c.ly} stroke="var(--oxide)" strokeWidth="1" strokeDasharray="2,2" />
          <circle cx={c.lx} cy={c.ly} r="10" fill="var(--paper)" stroke="var(--oxide)" strokeWidth="1" />
          <text x={c.lx} y={c.ly + 3} textAnchor="middle" fontSize="8" fontFamily="JetBrains Mono" fill="var(--oxide)">{c.num}</text>
          <text x={c.lx} y={c.ly + 22} textAnchor="middle" fontSize="7" fontFamily="JetBrains Mono" fill="var(--bone-2)" letterSpacing="1">{c.label}</text>
        </g>
      ))}

      {/* === Live coordinate readout (top-right corner) === */}
      <g fontFamily="JetBrains Mono" fontSize="8.5" fill="var(--bone)">
        <rect x="338" y="6" width="118" height="34" fill="var(--paper)" stroke="var(--rule)" />
        <text x="345" y="16" fill="var(--bone-2)" fontSize="7" letterSpacing="1">DRO · POSITION</text>
        <text x="345" y="28">X <tspan fill="var(--oxide)">{String(xMM).padStart(4, "0")}</tspan></text>
        <text x="395" y="28">Y <tspan fill="var(--bone)">{String(yMM).padStart(4, "0")}</tspan></text>
        <text x="345" y="36">Z <tspan fill="var(--oxide)">{String(zMM).padStart(3, "0")}</tspan></text>
        <text x="395" y="36">{rpm} RPM</text>
      </g>

      {/* === Dimension line: working envelope === */}
      <g stroke="var(--bone-2)" strokeWidth="1" fill="none">
        <line x1="80" y1="258" x2="440" y2="258" />
        <line x1="80" y1="254" x2="80" y2="262" />
        <line x1="440" y1="254" x2="440" y2="262" />
      </g>
      <text x="260" y="270" textAnchor="middle" fontSize="9" fontFamily="JetBrains Mono" fill="var(--bone-2)">WORK ENVELOPE 2400 × 600 × 380 MM</text>
    </svg>
  );
}

function Hero() {
  return (
    <section style={{ position: "relative", paddingTop: 24, paddingBottom: 0 }}>
      <div className="grid-bg" style={{ position: "absolute", inset: 0, opacity: .5, pointerEvents: "none" }} />

      <div className="wrap" style={{ position: "relative" }}>
        {/* Top status bar */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "18px 0", borderBottom: "1px solid var(--rule)",
        }}>
          <div className="mono" style={{ fontSize: 11, letterSpacing: ".14em", color: "var(--bone-2)" }}>
            <span className="dot live" style={{ marginRight: 8 }}></span>
            MACHINERY/IQ &nbsp;·&nbsp; FIELD UPLINK ACTIVE &nbsp;·&nbsp; 41 SITES &nbsp;·&nbsp; 1,284 ASSETS MONITORED
          </div>
          <div className="mono small muted hide-md">REV 04.26 &nbsp;·&nbsp; SHEET 01/04</div>
        </div>

        <div className="grid-hero" style={{
          gap: 56, paddingTop: 64, paddingBottom: 80, alignItems: "start",
        }}>
          {/* LEFT — headline */}
          <div>
            <div className="ulabel" style={{ marginBottom: 28 }}>
              <span style={{ color: "var(--oxide)" }}>§01</span> &nbsp;/&nbsp; INTELLIGENCE RETROFIT FOR INDUSTRIAL ASSETS
            </div>
            <h1 className="hero-h">
              Old&nbsp;iron.<br/>
              <em>New&nbsp;senses.</em>
            </h1>
            <div style={{
              marginTop: 36, maxWidth: 520, fontSize: 17, lineHeight: 1.55, color: "var(--bone-2)",
            }}>
              We install machine‑learning detection and early‑warning systems
              into machinery you already own. The lathe from 1994, the ore
              crusher commissioned under three different governments — they
              keep running. They just stop surprising you.
            </div>

            <div style={{ display: "flex", gap: 14, marginTop: 40, flexWrap: "wrap" }}>
              <a href="#contact" className="btn btn-oxide">
                Book a site assessment
                <span aria-hidden>→</span>
              </a>
              <a href="#how" className="btn btn-ghost">See the install process</a>
            </div>

            {/* Spec strip */}
            <div className="grid-3" style={{
              marginTop: 64,
              gap: 0, borderTop: "1px solid var(--rule)", borderBottom: "1px solid var(--rule)",
            }}>
              {[
                ["INSTALL", "5–9 days", "non-invasive sensors"],
                ["DOWNTIME ↓", "38% avg.", "across CNC fleets"],
                ["MTBF ↑", "2.4×", "post-retrofit median"],
              ].map(([k, v, sub], i) => (
                <div key={k} style={{
                  padding: "20px 22px",
                  borderLeft: i ? "1px solid var(--rule)" : "none",
                }}>
                  <div className="ulabel" style={{ marginBottom: 8 }}>{k}</div>
                  <div className="serif" style={{ fontSize: 32, lineHeight: 1 }}>{v}</div>
                  <div className="mono small muted" style={{ marginTop: 6 }}>{sub}</div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — telemetry hero */}
          <div className="frame telem" style={{ minHeight: 560 }}>
            <span className="tk tk-tl"></span><span className="tk tk-tr"></span>
            <span className="tk tk-bl"></span><span className="tk tk-br"></span>

            <div className="telem-hd">
              <span><span className="dot live" style={{ marginRight: 8 }}></span>LIVE · ASSET 04‑CNC‑MAZAK‑630</span>
              <span>UPTIME 04:21:08:44</span>
            </div>

            <div style={{ padding: 18, display: "grid", gap: 18 }}>
              <MachineSchematic />

              <div className="grid-2" style={{ gap: 18 }}>
                <Gauge label="SPINDLE LOAD" value={62} unit="%" warn={85} seed={0.7} />
                <Gauge label="VIB RMS" value={34} unit="mm/s" max={60} warn={48} seed={1.3} />
              </div>

              <div className="rule"></div>

              <Waveform seed={2} color="var(--oxide)" label="ACOUSTIC SIGNATURE — 8 KHZ BAND" value="−42.6" unit="dB" height={50} />
              <Waveform seed={5} color="var(--patina)" label="TORQUE — 3‑PHASE COMPOSITE" value="118" unit="N·m" height={50} />

              <div className="rule"></div>

              <ScrollingLog />
            </div>
          </div>
        </div>
      </div>

      {/* Ticker */}
      <div className="ticker">
        <div className="ticker-track mono">
          {Array.from({ length: 2 }).flatMap((_, j) => [
            <span key={`a${j}`}>↳ ASSET 04‑CNC‑MAZAK‑630 · <span className="v">SPINDLE LOAD 62%</span></span>,
            <span key={`b${j}`}>◇ ASSET 12‑GRD‑SVED‑M50 · <span className="v">VIB RMS 14 mm/s</span></span>,
            <span key={`c${j}`}>⚠ ASSET 18‑HYD‑WARTSILA · <span className="alarm">BEARING FATIGUE 148H ±12H</span></span>,
            <span key={`d${j}`}>↳ ASSET 03‑MILL‑DMG‑NHX · <span className="v">TOOL WEAR 31%</span></span>,
            <span key={`e${j}`}>◇ ASSET 27‑ORE‑METSO‑HP4 · <span className="v">ACOUSTIC NOMINAL</span></span>,
            <span key={`f${j}`}>⚠ ASSET 09‑PRESS‑SCHULER · <span className="alarm">LUBE INTERVAL 72H</span></span>,
            <span key={`g${j}`}>↳ ASSET 14‑LATHE‑OKUMA · <span className="v">POWER FACTOR 0.94</span></span>,
          ])}
        </div>
      </div>
    </section>
  );
}

window.Hero = Hero;


// ===== sections.jsx =====
// sections.jsx — Nav, How it works, Tech stack, CTA, Footer
const { useState: useStateS, useEffect: useEffectS } = React;

function Nav() {
  const [navOpen, setNavOpen] = useStateS(false);
  const closeNav = () => setNavOpen(false);
  return (
    <nav className="nav">
      <div className="wrap grid-nav" style={{
        alignItems: "center", gap: 32, padding: "16px 32px",
      }}>
        <a href="#" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
          <svg width="22" height="22" viewBox="0 0 22 22">
            <rect x="1" y="1" width="20" height="20" fill="none" stroke="var(--bone)" strokeWidth="1" />
            <line x1="1" y1="11" x2="21" y2="11" stroke="var(--oxide)" strokeWidth="1.5" />
            <line x1="11" y1="1" x2="11" y2="21" stroke="var(--oxide)" strokeWidth="1.5" />
            <circle cx="11" cy="11" r="3" fill="var(--oxide)" />
          </svg>
          <span className="mono" style={{ fontSize: 13, letterSpacing: ".14em", fontWeight: 600 }}>
            MACHINERY<span className="oxide">/</span>IQ
          </span>
        </a>
        <ul className={"mono hide-md nav-links" + (navOpen ? " open" : "")} style={{
          display: "flex", gap: 28, listStyle: "none", margin: 0, padding: 0,
          fontSize: 11.5, letterSpacing: ".12em", textTransform: "uppercase",
          color: "var(--bone-2)", justifyContent: "center",
        }}>
          <li><a href="#how" onClick={closeNav} style={{ textDecoration: "none" }}>Process</a></li>
          <li><a href="#stack" onClick={closeNav} style={{ textDecoration: "none" }}>Stack</a></li>
          <li><a href="#roi" onClick={closeNav} style={{ textDecoration: "none" }}>ROI</a></li>
          <li><a href="#contact" onClick={closeNav} style={{ textDecoration: "none" }}>Contact</a></li>
        </ul>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <a href="#contact" className="btn btn-ghost nav-cta-desktop" style={{ padding: "10px 16px" }}>
            Site assessment
          </a>
          <button
            type="button"
            className="nav-burger"
            aria-label={navOpen ? "Close menu" : "Open menu"}
            aria-expanded={navOpen}
            onClick={() => setNavOpen(o => !o)}
          >
            <span></span>
          </button>
        </div>
      </div>
    </nav>
  );
}

function SectionHead({ num, title, meta }) {
  return (
    <div className="section-head">
      <div className="section-num">§ {num}</div>
      <h2 className="section-title serif">{title}</h2>
      <div className="section-meta hide-md">{meta}</div>
    </div>
  );
}

function HowItWorks() {
  const steps = [
    {
      num: "01",
      title: "Audit",
      duration: "1–2 days",
      body: "We walk your floor with reliability engineers. Catalogue every asset, age, control system, prior failure modes. No NDA theatre — concrete failure data, where it lives, what it costs you.",
      bullet: ["Asset inventory", "Failure-mode catalogue", "Floor & PLC mapping"],
    },
    {
      num: "02",
      title: "Instrument",
      duration: "3–5 days",
      body: "Non-invasive sensor packs — vibration, temperature, acoustic, current — clamp onto existing machinery. No drilling into housings, no PLC firmware changes. Signal aggregation lives in a shielded edge cabinet.",
      bullet: ["Sensor pack install", "Edge gateway provisioning", "Baseline capture (72h)"],
    },
    {
      num: "03",
      title: "Train",
      duration: "2–6 weeks",
      body: "Models train on your machine, not a generic one. Bearing harmonics from your specific 31‑year‑old grinder are not the textbook ones. We learn your normal first, then we learn your failures.",
      bullet: ["Per-asset baselines", "Anomaly detection", "Failure-mode classifiers"],
    },
    {
      num: "04",
      title: "Run",
      duration: "ongoing",
      body: "Early-warning notifications route to whoever fixes things — SMS, email, WhatsApp, a phone call. We handle the messaging end-to-end and can organise replacement parts and repair visits, or just notify your main point of contact. Nothing for your team to install or learn.",
      bullet: ["Alerting & routing", "Parts & repair coordination", "Quarterly model tune"],
    },
  ];
  return (
    <section id="how" style={{ padding: "120px 0", borderTop: "1px solid var(--rule)", position: "relative", overflow: "hidden" }}>
      <div aria-hidden="true" style={{
        position: "absolute", inset: 0,
        backgroundImage: "url('assets/factory-bg.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        filter: "grayscale(.5) contrast(1.05) brightness(.45)",
        opacity: .42,
        zIndex: 0,
      }} />
      <div aria-hidden="true" style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(180deg, var(--paper) 0%, rgba(14,15,13,.6) 18%, rgba(14,15,13,.55) 82%, var(--paper) 100%)",
        zIndex: 1,
      }} />
      <div aria-hidden="true" className="grid-bg" style={{
        position: "absolute", inset: 0, opacity: .35, zIndex: 1, pointerEvents: "none",
      }} />
      <div className="wrap" style={{ position: "relative", zIndex: 2 }}>
        <SectionHead num="02" title={<>How we <em style={{ color: "var(--oxide)", fontStyle: "italic" }}>install</em> intelligence.</>} meta="PROCESS · 04 PHASES" />

        <div style={{ display: "grid", gap: 0 }}>
          {steps.map((s, i) => (
            <div key={s.num} className="grid-process" style={{
              gap: 40, alignItems: "start",
              padding: "44px 0",
              borderTop: "1px solid var(--rule)",
              borderBottom: i === steps.length - 1 ? "1px solid var(--rule)" : "none",
            }}>
              <div className="step-num">{s.num}</div>
              <div>
                <div className="serif" style={{ fontSize: 38, lineHeight: 1, marginBottom: 8 }}>{s.title}</div>
                <div className="ulabel"><span className="oxide">●</span>&nbsp;&nbsp;DURATION · {s.duration}</div>
              </div>
              <div style={{ fontSize: 15, lineHeight: 1.55, color: "var(--bone-2)", maxWidth: 460 }}>
                {s.body}
              </div>
              <ul className="mono" style={{
                margin: 0, padding: 0, listStyle: "none", fontSize: 11.5, letterSpacing: ".06em",
                color: "var(--bone)",
              }}>
                {s.bullet.map(b => (
                  <li key={b} style={{ padding: "6px 0", borderBottom: "1px solid var(--rule-2)" }}>
                    <span className="muted" style={{ marginRight: 10 }}>↳</span>{b}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Dimension line summary */}
        <div className="dim-line" style={{ marginTop: 28 }}>
          <span>T0</span>
          <div className="bar"></div>
          <span>T+12W &nbsp; FULL OPERATION</span>
        </div>
      </div>
    </section>
  );
}

function StackFolder({ index, group }) {
  const [open, setOpen] = React.useState(false);
  const [pinned, setPinned] = React.useState(false);
  const expanded = open || pinned;
  const contentRef = React.useRef(null);
  const [maxH, setMaxH] = React.useState(0);
  React.useEffect(() => {
    if (contentRef.current) setMaxH(contentRef.current.scrollHeight);
  }, [group]);

  return (
    <div
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      style={{
        marginBottom: 16,
        border: "1px solid var(--rule)",
        background: expanded ? "rgba(232,228,220,.02)" : "transparent",
        transition: "background .25s ease, border-color .25s ease",
        borderColor: expanded ? "var(--bone-2)" : "var(--rule)",
      }}
    >
      <button
        type="button"
        onClick={() => setPinned(p => !p)}
        className="grid-stack-folder-hd"
        style={{
          width: "100%",
          alignItems: "center", gap: 18,
          padding: "20px 22px",
          background: "transparent", border: 0, cursor: "pointer",
          color: "inherit", textAlign: "left",
        }}
      >
        <span className="serif" style={{ fontSize: 28, fontStyle: "italic", color: expanded ? "var(--oxide)" : "var(--bone)" }}>
          {String(index + 1).padStart(2, "0")}
        </span>
        <span aria-hidden style={{
          width: 14, height: 14, position: "relative",
          transform: expanded ? "rotate(45deg)" : "rotate(0deg)",
          transition: "transform .25s ease",
        }}>
          <span style={{ position: "absolute", inset: 0, margin: "auto", width: 14, height: 1, background: "var(--bone)", top: "50%" }} />
          <span style={{ position: "absolute", inset: 0, margin: "auto", width: 1, height: 14, background: "var(--bone)", left: "50%" }} />
        </span>
        <span className="ulabel" style={{ color: "var(--bone)" }}>{group.group}</span>
        <span className="mono small muted">{group.items.length} ITEMS</span>
        <span className="mono small" style={{ color: "var(--patina)" }}>● INCLUDED</span>
      </button>
      <div style={{
        maxHeight: expanded ? maxH : 0,
        overflow: "hidden",
        transition: "max-height .35s ease",
      }}>
        <div ref={contentRef} style={{ padding: "0 22px 8px", borderTop: "1px solid var(--rule)" }}>
          {group.items.map(([name, desc, tag]) => (
            <div key={name} className="stack-row">
              <div className="mono small muted">{tag}</div>
              <div>
                <div className="stack-name serif">{name}</div>
                <div className="mono small muted" style={{ marginTop: 4 }}>{desc}</div>
              </div>
              <div className="mono small muted hide-md" style={{ textAlign: "right" }}>SHIPS WITH UNIT</div>
              <div style={{ textAlign: "right" }}>
                <span className="mono small" style={{ color: "var(--patina)" }}>● INCLUDED</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TechStack() {
  const stack = [
    {
      group: "ON THE MACHINE",
      items: [
        ["Vibration sensor pack", "3-axis MEMS · 0.1–10 kHz · IP67", "MECH"],
        ["Acoustic capsule", "Wide-band · 20 Hz – 80 kHz · ultrasonic", "MECH"],
        ["Current clamp array", "Per-phase · non-contact · isolated", "ELEC"],
        ["Thermal pads", "Bearing housings · −40 to 125°C", "MECH"],
      ],
    },
    {
      group: "AT THE EDGE",
      items: [
        ["MACHINERY/EDGE gateway", "ARM · ruggedised · DIN-rail · OTA", "EDGE"],
        ["Local inference runtime", "ONNX · 30–120 ms latency", "ML"],
        ["Buffered store", "30 days local · MQTT outbound", "DATA"],
      ],
    },
    {
      group: "IN THE MODEL",
      items: [
        ["Per-asset baseline learner", "Online · drift-aware · 72h cold start", "ML"],
        ["Anomaly detector", "Variational + spectral hybrid", "ML"],
        ["Failure-mode classifier", "Bearing · gearbox · lubrication · electrical", "ML"],
        ["Remaining-life estimator", "Probabilistic · calibrated CIs", "ML"],
      ],
    },
    {
      group: "WHAT YOU GET",
      items: [
        ["Alert routing", "SMS · email · WhatsApp · phone call", "INT"],
        ["Single point of contact", "Named reliability engineer · weekly digest", "OPS"],
        ["Parts coordination", "Sourced & billed at cost · OEM or equivalent", "OPS"],
        ["Repair visits", "Scheduled around your production calendar", "OPS"],
      ],
    },
  ];
  return (
    <section id="stack" style={{ padding: "120px 0", borderTop: "1px solid var(--rule)", background: "#0a0b09" }}>
      <div className="wrap">
        <SectionHead num="03" title={<>What gets <em style={{ color: "var(--oxide)", fontStyle: "italic" }}>installed.</em></>} meta="STACK · 14 COMPONENTS" />

        <div className="grid-stack-side" style={{ gap: 56 }}>
          <div className="hide-md">
            <div className="ulabel" style={{ marginBottom: 16 }}>SECTIONS</div>
            <ul className="mono" style={{
              listStyle: "none", margin: 0, padding: 0, fontSize: 11.5, letterSpacing: ".08em",
              lineHeight: 1.9,
            }}>
              {stack.map(s => (
                <li key={s.group}><a href={`#${s.group}`} className="muted" style={{ textDecoration: "none" }}>↳ {s.group}</a></li>
              ))}
            </ul>

            <div style={{ marginTop: 48, padding: 18, border: "1px solid var(--rule)" }}>
              <div className="ulabel" style={{ marginBottom: 10, color: "var(--oxide)" }}>NOTE</div>
              <div className="mono" style={{ fontSize: 11.5, lineHeight: 1.7, color: "var(--bone-2)" }}>
                No new applications, dashboards or software for your team to install or learn. Sensors are clamp-on, current is non-contact, and the edge gateway speaks only outward — to us. You hear from us; we handle the rest.
              </div>
            </div>
          </div>

          <div>
            {stack.map((g, gi) => (
              <StackFolder key={g.group} index={gi} group={g} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactCTA() {
  return (
    <section id="contact" style={{ padding: "140px 0", borderTop: "1px solid var(--rule)", position: "relative", overflow: "hidden" }}>
      <div className="grid-bg" style={{ position: "absolute", inset: 0, opacity: .35, pointerEvents: "none" }} />
      <div className="wrap" style={{ position: "relative" }}>
        <div className="grid-cta" style={{ gap: 80, alignItems: "end" }}>
          <div>
            <div className="ulabel" style={{ marginBottom: 32 }}>
              <span className="oxide">§05</span> &nbsp;/&nbsp; INTERESTED IN EXPLORING SIMPLE AI SOLUTIONS?
            </div>
            <h2 className="serif" style={{
              fontSize: "clamp(48px,6.4vw,108px)", lineHeight: .95, letterSpacing: "-.01em", margin: 0,
            }}>
              Bring us<br/>
              your <em style={{ color: "var(--oxide)", fontStyle: "italic" }}>oldest</em><br/>
              machine.
            </h2>
            <p style={{ marginTop: 32, maxWidth: 480, fontSize: 17, color: "var(--bone-2)", lineHeight: 1.55 }}>
              The one held together with operator intuition and a stack of OEM
              manuals from 1998. We'll tell you, free, whether intelligence
              retrofit is going to pay for itself — and how fast.
            </p>
          </div>
          <div className="frame card" style={{ background: "#0a0b09" }}>
            <span className="tk tk-tl"></span><span className="tk tk-tr"></span>
            <span className="tk tk-bl"></span><span className="tk tk-br"></span>
            <div className="ulabel" style={{ marginBottom: 18 }}>BOOK A SITE ASSESSMENT</div>
            <form onSubmit={(e) => { e.preventDefault(); alert("Thank you — a reliability engineer will be in touch within one business day."); }}>
              {[
                ["NAME", "text", "Jane Operator"],
                ["COMPANY / SITE", "text", "Acme Forge — Sheffield"],
                ["EMAIL", "email", "jane@acme.example"],
                ["FLEET SIZE", "text", "24 CNC, 6 grinders"],
              ].map(([lbl, type, ph]) => (
                <label key={lbl} style={{ display: "block", marginBottom: 16 }}>
                  <div className="ulabel" style={{ marginBottom: 6 }}>{lbl}</div>
                  <input
                    type={type}
                    placeholder={ph}
                    style={{
                      width: "100%", padding: "12px 14px", background: "transparent",
                      border: "1px solid var(--rule)", color: "var(--bone)",
                      fontFamily: "JetBrains Mono, monospace", fontSize: 13,
                      outline: "none",
                    }}
                    onFocus={(e) => e.target.style.borderColor = "var(--oxide)"}
                    onBlur={(e) => e.target.style.borderColor = "var(--rule)"}
                  />
                </label>
              ))}
              <button type="submit" className="btn btn-oxide" style={{ width: "100%", justifyContent: "center", marginTop: 8 }}>
                Request assessment <span aria-hidden>→</span>
              </button>
              <div className="mono small muted" style={{ marginTop: 14, textAlign: "center" }}>
                Response within 1 business day · No obligation
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer style={{ padding: "48px 0 36px", borderTop: "1px solid var(--rule)" }}>
      <div className="wrap grid-footer" style={{ gap: 24, alignItems: "center" }}>
        <div className="mono small muted">
          MACHINERY/IQ &nbsp;·&nbsp; INTELLIGENCE RETROFIT FOR INDUSTRIAL ASSETS &nbsp;·&nbsp; EST. 2024
        </div>
        <div className="mono small muted">
          DRAWING NO. FIQ‑LP‑01 &nbsp;·&nbsp; SHEET 04/04 &nbsp;·&nbsp; REV 04.26
        </div>
      </div>
    </footer>
  );
}

window.Nav = Nav;
window.HowItWorks = HowItWorks;
window.TechStack = TechStack;
window.ContactCTA = ContactCTA;
window.Footer = Footer;
window.SectionHead = SectionHead;


// ===== roi.jsx =====
// roi.jsx — ROI calculator with real-feeling sliders
const { useState: useStateR, useMemo: useMemoR } = React;

function NumberDisplay({ value, unit, prefix }) {
  return (
    <span className="mono">
      {prefix}<span style={{ color: "var(--bone)" }}>{value}</span>
      <span className="muted" style={{ marginLeft: 4, fontSize: ".8em" }}>{unit}</span>
    </span>
  );
}

function ROISlider({ label, min, max, step, value, onChange, unit, format }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <span className="ulabel">{label}</span>
        <span className="mono" style={{ fontSize: 14, color: "var(--oxide)" }}>
          {format ? format(value) : value}
          <span className="muted" style={{ marginLeft: 4 }}>{unit}</span>
        </span>
      </div>
      <input
        type="range" className="slider" min={min} max={max} step={step}
        value={value} onChange={(e) => onChange(Number(e.target.value))}
      />
      <div className="dim-line" style={{ marginTop: -12 }}>
        <span>{format ? format(min) : min}</span>
        <div className="bar"></div>
        <span>{format ? format(max) : max}</span>
      </div>
    </div>
  );
}

function ROISection() {
  const [assets, setAssets] = useStateR(24);
  const [downtime, setDowntime] = useStateR(8); // % of time
  const [hourCost, setHourCost] = useStateR(2200); // $ per hour
  const [shifts, setShifts] = useStateR(2); // shifts/day

  const calc = useMemoR(() => {
    const hoursPerDay = shifts * 8;
    const yearHours = hoursPerDay * 250 * assets; // 250 production days
    const downtimeHours = yearHours * (downtime / 100);
    const downtimeCost = downtimeHours * hourCost;
    // Machinery/IQ delivers ~38% downtime reduction
    const reduction = 0.38;
    const saved = downtimeCost * reduction;
    const fiveYr = saved * 5;
    return {
      downtimeHours: Math.round(downtimeHours),
      downtimeCost: Math.round(downtimeCost),
      savedHours: Math.round(downtimeHours * reduction),
      saved: Math.round(saved),
      fiveYr: Math.round(fiveYr),
    };
  }, [assets, downtime, hourCost, shifts]);

  const fmt$ = (n) => "£" + Math.round(n).toLocaleString();
  const fmtK = (n) => {
    if (n >= 1_000_000) return "£" + (n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1) + "M";
    if (n >= 1000) return "£" + (n / 1000).toFixed(n >= 10000 ? 0 : 1) + "k";
    return "£" + n;
  };

  return (
    <section id="roi" style={{ padding: "120px 0", borderTop: "1px solid var(--rule)" }}>
      <div className="wrap">
        <SectionHead num="04" title={<>Run the <em style={{ color: "var(--oxide)", fontStyle: "italic" }}>numbers.</em></>} meta="CALCULATOR · LIVE" />

        <div className="grid-roi-split" style={{ gap: 64, alignItems: "start" }}>
          {/* INPUTS */}
          <div className="frame" style={{ padding: 36, background: "#0a0b09" }}>
            <span className="tk tk-tl"></span><span className="tk tk-tr"></span>
            <span className="tk tk-bl"></span><span className="tk tk-br"></span>
            <div className="ulabel" style={{ marginBottom: 24 }}>YOUR FLEET</div>

            <ROISlider
              label="ASSETS UNDER MANAGEMENT"
              min={4} max={120} step={1}
              value={assets} onChange={setAssets} unit="machines"
            />
            <ROISlider
              label="UNPLANNED DOWNTIME"
              min={1} max={25} step={0.5}
              value={downtime} onChange={setDowntime} unit="% of production hours"
              format={(v) => v}
            />
            <ROISlider
              label="DOWNTIME COST"
              min={400} max={12000} step={100}
              value={hourCost} onChange={setHourCost} unit="per hour"
              format={fmt$}
            />
            <ROISlider
              label="SHIFTS PER DAY"
              min={1} max={3} step={1}
              value={shifts} onChange={setShifts} unit={shifts === 1 ? "shift" : "shifts"}
            />

            <div className="rule" style={{ margin: "28px 0 20px" }}></div>
            <div className="mono small muted" style={{ lineHeight: 1.6 }}>
              ASSUMES 250 PRODUCTION DAYS · 8H SHIFTS · 38% DOWNTIME REDUCTION (MACHINERY/IQ FIELD MEDIAN, n=41 INSTALLS)
            </div>
          </div>

          {/* OUTPUTS */}
          <div>
            <div className="ulabel" style={{ marginBottom: 24 }}>YEAR 01 · PROJECTED</div>

            <div className="grid-2" style={{ gap: 0,
              border: "1px solid var(--rule)" }}>
              <div style={{ padding: 28, borderRight: "1px solid var(--rule)", minWidth: 0, overflow: "hidden" }}>
                <div className="ulabel" style={{ marginBottom: 8 }}>DOWNTIME COST · TODAY</div>
                <div className="bignum" style={{ fontSize: "clamp(40px,5.4vw,96px)" }}>{fmtK(calc.downtimeCost)}</div>
                <div className="mono small muted" style={{ marginTop: 10 }}>
                  ≈ {calc.downtimeHours.toLocaleString()} hrs/year lost
                </div>
              </div>
              <div style={{ padding: 28, background: "rgba(200,82,26,.05)", minWidth: 0, overflow: "hidden" }}>
                <div className="ulabel" style={{ marginBottom: 8, color: "var(--oxide)" }}>RECOVERED</div>
                <div className="bignum oxide" style={{ fontSize: "clamp(40px,5.4vw,96px)" }}>{fmtK(calc.saved)}</div>
                <div className="mono small muted" style={{ marginTop: 10 }}>
                  ≈ {calc.savedHours.toLocaleString()} hrs returned to production
                </div>
              </div>

              <div style={{ padding: 28, borderTop: "1px solid var(--rule)", borderRight: "1px solid var(--rule)", minWidth: 0, overflow: "hidden" }}>
                <div className="ulabel" style={{ marginBottom: 8 }}>HRS RETURNED · YEAR 01</div>
                <div className="serif" style={{ fontSize: "clamp(28px,3.2vw,44px)", lineHeight: 1 }}>{calc.savedHours.toLocaleString()}<sup style={{ fontSize: ".5em", color: "var(--bone-2)", fontFamily: "JetBrains Mono", letterSpacing: ".1em", marginLeft: 6 }}>HRS</sup></div>
                <div className="mono small muted" style={{ marginTop: 10 }}>
                  back into production
                </div>
              </div>

              <div style={{ padding: 28, borderTop: "1px solid var(--rule)", minWidth: 0, overflow: "hidden" }}>
                <div className="ulabel" style={{ marginBottom: 8 }}>FAILURES AVOIDED</div>
                <div className="serif" style={{ fontSize: "clamp(28px,3.2vw,44px)", lineHeight: 1 }}>
                  {Math.max(1, Math.round(assets * 0.6))}<sup style={{ fontSize: ".5em", color: "var(--bone-2)", fontFamily: "JetBrains Mono", letterSpacing: ".1em", marginLeft: 6 }}>EST·YR</sup>
                </div>
                <div className="mono small muted" style={{ marginTop: 10 }}>
                  caught before unplanned stop
                </div>
              </div>
            </div>

            {/* 5 year recovered */}
            <div style={{ marginTop: 32 }}>
              <div className="ulabel" style={{ marginBottom: 14 }}>5‑YEAR DOWNTIME RECOVERED</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap" }}>
                <div className="bignum oxide" style={{ fontSize: "clamp(48px,6vw,84px)" }}>
                  {fmtK(calc.fiveYr)}
                </div>
                <div className="mono small muted">
                  ↳ recovered downtime · 5 yr horizon
                </div>
              </div>

              <div style={{ marginTop: 22, padding: "16px 18px", border: "1px solid var(--rule)", display: "flex", gap: 18, alignItems: "flex-start" }}>
                <span className="mono" style={{ fontSize: 11, color: "var(--oxide)", letterSpacing: ".12em", marginTop: 2 }}>NOTE</span>
                <div className="mono small muted" style={{ lineHeight: 1.65 }}>
                  We don't quote a fixed program cost. <span style={{ color: "var(--bone)" }}>Parts are billed at cost</span>, plus a one-off installation fee scoped against your floor walk. Numbers above are the upside; we'll calibrate the investment against it together.
                </div>
              </div>
            </div>

            <a href="#contact" className="btn btn-oxide" style={{ marginTop: 28 }}>
              Get a calibrated quote <span aria-hidden>→</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

window.ROISection = ROISection;


// ===== app.jsx =====
// app.jsx — Compose, mount, Tweaks
const { useEffect: useEffectA } = React;

const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "accent": "#c8521a",
  "secondary": "#5b6b3a",
  "headlineFont": "Instrument Serif",
  "headlineStyle": "industrial",
  "background": "#0e0f0d",
  "showTicker": true,
  "showSchematic": true,
  "showTelemetry": true
}/*EDITMODE-END*/;

const HEADLINE_VARIANTS = {
  industrial: { eyebrow: "INTELLIGENCE RETROFIT FOR INDUSTRIAL ASSETS", h: <>Old&nbsp;iron.<br/><em>New&nbsp;senses.</em></>, sub: "We install machine‑learning detection and early‑warning systems into machinery you already own. The lathe from 1994, the ore crusher commissioned under three different governments — they keep running. They just stop surprising you." },
  technical: { eyebrow: "MACHINE LEARNING · PREDICTIVE MAINTENANCE", h: <>The machine<br/><em>knows itself</em><br/>now.</>, sub: "Non-invasive sensor packs and per-asset machine-learning models retrofitted into legacy industrial equipment. Detect bearing fatigue 148 hours before it bites. Schedule the shutdown. Sleep at night." },
  blunt: { eyebrow: "STOP MACHINES BREAKING UNEXPECTEDLY", h: <><em>Predict</em><br/>the failure.</>, sub: "We bolt sensors onto your existing industrial machinery — lathes, crushers, presses, grinders — and train models that learn what normal sounds like for that specific machine. Alarms ring before things break, not after." },
};

function App() {
  const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);

  // Apply theming via CSS vars
  useEffectA(() => {
    document.documentElement.style.setProperty('--oxide', t.accent);
    document.documentElement.style.setProperty('--patina', t.secondary);
    document.documentElement.style.setProperty('--paper', t.background);
    document.body.style.background = t.background;
  }, [t.accent, t.secondary, t.background]);

  // Override headline variant by mutating Hero is heavy; instead, render here
  // and pass a variant down via window. Simpler: re-render the whole hero.
  return (
    <>
      <Nav />
      <HeroVariant variant={t.headlineStyle} headlineFont={t.headlineFont} showTicker={t.showTicker} showSchematic={t.showSchematic} showTelemetry={t.showTelemetry} />
      <HowItWorks />
      <TechStack />
      <ROISection />
      <ContactCTA />
      <Footer />

      <TweaksPanel>
        <TweakSection label="Headline" />
        <TweakRadio label="Voice" value={t.headlineStyle}
          options={["industrial", "technical", "blunt"]}
          onChange={(v) => setTweak('headlineStyle', v)} />
        <TweakSelect label="Headline font" value={t.headlineFont}
          options={["Instrument Serif", "JetBrains Mono", "Inter Tight"]}
          onChange={(v) => setTweak('headlineFont', v)} />

        <TweakSection label="Palette" />
        <TweakColor label="Accent" value={t.accent} onChange={(v) => setTweak('accent', v)} />
        <TweakColor label="Secondary" value={t.secondary} onChange={(v) => setTweak('secondary', v)} />
        <TweakColor label="Background" value={t.background} onChange={(v) => setTweak('background', v)} />

        <TweakSection label="Hero" />
        <TweakToggle label="Show telemetry panel" value={t.showTelemetry} onChange={(v) => setTweak('showTelemetry', v)} />
        <TweakToggle label="Show machine schematic" value={t.showSchematic} onChange={(v) => setTweak('showSchematic', v)} />
        <TweakToggle label="Show live ticker" value={t.showTicker} onChange={(v) => setTweak('showTicker', v)} />

        <TweakSection label="Reset" />
        <TweakButton onClick={() => {
          setTweak({
            accent: "#c8521a", secondary: "#5b6b3a", background: "#0e0f0d",
            headlineFont: "Instrument Serif", headlineStyle: "industrial",
            showTicker: true, showSchematic: true, showTelemetry: true,
          });
        }}>Reset all tweaks</TweakButton>
      </TweaksPanel>
    </>
  );
}

// HeroVariant wraps Hero but allows different headlines / hides parts.
// Instead of editing Hero, re-render a thin variant copy.
function HeroVariant({ variant, headlineFont, showTicker, showSchematic, showTelemetry }) {
  const v = HEADLINE_VARIANTS[variant] || HEADLINE_VARIANTS.industrial;
  const headlineFontStack = headlineFont === "Instrument Serif"
    ? "'Instrument Serif', serif"
    : headlineFont === "JetBrains Mono"
    ? "'JetBrains Mono', monospace"
    : "'Inter Tight', sans-serif";
  return (
    <section style={{ position: "relative", paddingTop: 24 }}>
      <div className="grid-bg" style={{ position: "absolute", inset: 0, opacity: .5, pointerEvents: "none" }} />
      <div className="wrap" style={{ position: "relative" }}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "18px 0", borderBottom: "1px solid var(--rule)",
        }}>
          <div className="mono" style={{ fontSize: 11, letterSpacing: ".14em", color: "var(--bone-2)" }}>
            <span className="dot live" style={{ marginRight: 8 }}></span>
            MACHINERY/IQ &nbsp;·&nbsp; FIELD UPLINK ACTIVE &nbsp;·&nbsp; 41 SITES &nbsp;·&nbsp; 1,284 ASSETS MONITORED
          </div>
          <div className="mono small muted hide-md">REV 04.26 &nbsp;·&nbsp; SHEET 01/04</div>
        </div>

        <div className={showTelemetry ? "grid-hero" : "grid-hero-solo"} style={{
          gap: 56, paddingTop: 64, paddingBottom: 80, alignItems: "start",
        }}>
          <div>
            <div className="ulabel" style={{ marginBottom: 28 }}>
              <span style={{ color: "var(--oxide)" }}>§01</span> &nbsp;/&nbsp; {v.eyebrow}
            </div>
            <h1 className="hero-h" style={{
              fontFamily: headlineFontStack,
              fontStyle: headlineFont === "Instrument Serif" ? "normal" : "normal",
              fontWeight: headlineFont === "Instrument Serif" ? 400 : 600,
              fontSize: headlineFont === "Instrument Serif" ? "clamp(54px,7.6vw,128px)" : "clamp(44px,5.8vw,96px)",
              letterSpacing: headlineFont === "JetBrains Mono" ? "-.04em" : "-.01em",
              textTransform: headlineFont === "JetBrains Mono" ? "uppercase" : "none",
            }}>
              {v.h}
            </h1>
            <div style={{ marginTop: 36, maxWidth: 520, fontSize: 17, lineHeight: 1.55, color: "var(--bone-2)" }}>
              {v.sub}
            </div>
            <div style={{ display: "flex", gap: 14, marginTop: 40, flexWrap: "wrap" }}>
              <a href="#contact" className="btn btn-oxide">Book a site assessment <span aria-hidden>→</span></a>
              <a href="#how" className="btn btn-ghost">See the install process</a>
            </div>
            <div className="grid-3" style={{
              marginTop: 64,
              gap: 0, borderTop: "1px solid var(--rule)", borderBottom: "1px solid var(--rule)",
            }}>
              {[
                ["INSTALL", "5–9 days", "non-invasive sensors"],
                ["DOWNTIME ↓", "38% avg.", "across CNC fleets"],
                ["MTBF ↑", "2.4×", "post-retrofit median"],
              ].map(([k, val, sub], i) => (
                <div key={k} style={{ padding: "20px 22px", borderLeft: i ? "1px solid var(--rule)" : "none" }}>
                  <div className="ulabel" style={{ marginBottom: 8 }}>{k}</div>
                  <div className="serif" style={{ fontSize: 32, lineHeight: 1 }}>{val}</div>
                  <div className="mono small muted" style={{ marginTop: 6 }}>{sub}</div>
                </div>
              ))}
            </div>
          </div>

          {showTelemetry && (
            <div className="frame telem" style={{ minHeight: 560 }}>
              <span className="tk tk-tl"></span><span className="tk tk-tr"></span>
              <span className="tk tk-bl"></span><span className="tk tk-br"></span>
              <div className="telem-hd">
                <span><span className="dot live" style={{ marginRight: 8 }}></span>LIVE · ASSET 04‑DRL‑KALTENBACH‑KDX</span>
                <span>UPTIME 04:21:08:44</span>
              </div>
              <div style={{ padding: 18, display: "grid", gap: 18 }}>
                {showSchematic && <MachineSchematic />}
                <div className="grid-2" style={{ gap: 18 }}>
                  <Gauge label="SPINDLE LOAD" value={62} unit="%" warn={85} seed={0.7} />
                  <Gauge label="VIB RMS" value={34} unit="mm/s" max={60} warn={48} seed={1.3} />
                </div>
                <div className="rule"></div>
                <Waveform seed={2} color="var(--oxide)" label="ACOUSTIC SIGNATURE — 8 KHZ BAND" value="−42.6" unit="dB" height={50} />
                <Waveform seed={5} color="var(--patina)" label="TORQUE — 3‑PHASE COMPOSITE" value="118" unit="N·m" height={50} />
                <div className="rule"></div>
                <ScrollingLog />
              </div>
            </div>
          )}
        </div>
      </div>

      {showTicker && (
        <div className="ticker">
          <div className="ticker-track mono">
            {Array.from({ length: 2 }).flatMap((_, j) => [
              <span key={`a${j}`}>↳ ASSET 04‑DRL‑KALTENBACH‑KDX · <span className="v">SPINDLE LOAD 62%</span></span>,
              <span key={`b${j}`}>◇ ASSET 12‑GRD‑SVED‑M50 · <span className="v">VIB RMS 14 mm/s</span></span>,
              <span key={`c${j}`}>⚠ ASSET 18‑HYD‑WARTSILA · <span className="alarm">BEARING FATIGUE 148H ±12H</span></span>,
              <span key={`d${j}`}>↳ ASSET 03‑MILL‑DMG‑NHX · <span className="v">TOOL WEAR 31%</span></span>,
              <span key={`e${j}`}>◇ ASSET 27‑ORE‑METSO‑HP4 · <span className="v">ACOUSTIC NOMINAL</span></span>,
              <span key={`f${j}`}>⚠ ASSET 09‑PRESS‑SCHULER · <span className="alarm">LUBE INTERVAL 72H</span></span>,
              <span key={`g${j}`}>↳ ASSET 14‑LATHE‑OKUMA · <span className="v">POWER FACTOR 0.94</span></span>,
            ])}
          </div>
        </div>
      )}
    </section>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(<App />);
